import { ConfirmEmailBanner } from 'components/unconfirmed/confirm_email_banner'
import { TrixForm } from 'controls/forms/trix_form'
import { useHandleMetadata } from 'hooks/use_handle_metadata'
import { useTypingIndicator } from 'hooks/use_typing_indicator'
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { createNewLiveChat, sendChat } from 'reduxes/slices/chats/actions'
import { createDirectChat } from 'reduxes/slices/direct_chats/actions'
import { sendNote } from 'reduxes/slices/notes/actions'
import { getSelectedDirectChatHandle } from 'reduxes/slices/selected/selectors'
import { getSelectedTopic } from 'reduxes/slices/topics/selectors'
import apiRoutes from 'routes/admin_api_routes.json'

import { css } from 'design'
import { Button } from 'primitives/button'
import { Stack } from 'primitives/containers'
import { cn } from 'util/cn'

import { ApiErrorMessages } from '../../../controls/errors/api_error_messages'
import { useNavToDirectChat } from '../../../hooks/app_location/use_nav_to_direct_chat'
import { useNavToTopic } from '../../../hooks/app_location/use_nav_to_topic'
import { useAxios } from '../../../hooks/use_axios'
import { useGetPublicContact } from '../../../hooks/use_get_public_contact'
import { useRefCallback } from '../../../hooks/use_ref_callback'
import { MenuDeleteIcon } from '../../../images'
import {
  AppLocationContext,
  WIDGET_VIEW,
} from '../../../pages/admin/app_location_provider'
import { makeGetNamesForHandles } from '../../../reduxes/slices/contacts/selectors'
import {
  getCurrentHandle,
  getCurrentMemberId,
} from '../../../reduxes/slices/current/selectors'
import { isLiveChatApp } from '../../../reduxes/slices/live_chat_app/selectors'
import { makeGetParticipantsForTopic } from '../../../reduxes/slices/participants/selectors'
import { setReplyTimelineId } from '../../../reduxes/slices/timelines/actions'
import { getReplyTimeline } from '../../../reduxes/slices/timelines/selectors'
import { useSelectorDelegateStore } from '../../../reduxes/stores/root_store/hooks'
import { findRecordInApiResponse } from '../../../reduxes/util/api_response_utils'
import { GROUP_TOPIC_TYPES } from '../../../util/constants'
import { ReplyChatReference } from '../../timelines/items/reply_chat_reference'

const NOTE_PLACEHOLDER = 'Private note. Only you will see this'

export type ComposerType = 'message' | 'note'

export const Composer = () => {
  const dispatch = useDispatch()
  const axios = useAxios()

  const [activeType, setActiveType] = useState<ComposerType>('message')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const topic = useSelector<any, any>(getSelectedTopic)
  const directChatHandle = useSelector(getSelectedDirectChatHandle)
  const { name } = useHandleMetadata(directChatHandle)
  const replyTimeline = useSelector(getReplyTimeline)
  const focusRef = useRef<any>()
  const navToDirectChat = useNavToDirectChat()
  const navToTopic = useNavToTopic()
  const [composerElement, composerRefCallback] =
    useRefCallback<HTMLDivElement>()
  const liveChatApp = useSelectorDelegateStore(isLiveChatApp)

  const topicId = topic?.id

  // Begin postToApp data
  const getParticipants = useMemo(makeGetParticipantsForTopic, [])
  const topicParticipants = useSelector((state) =>
    // eslint-disable-next-line
    // @ts-ignore
    getParticipants(state, topic),
  )

  const getNamesForHandles = useMemo(makeGetNamesForHandles, [])
  const currentHandle = useSelector(getCurrentHandle)
  const currentMemberId = useSelector(getCurrentMemberId)
  const otherParticipants = topicParticipants.filter(
    (p: { handle: any }) => p.handle !== currentHandle,
  )
  const otherHandles = otherParticipants.map(
    (participant: any) => participant.handle,
  )
  const namesLookup = useSelector((state) =>
    getNamesForHandles(state, otherHandles),
  )

  const otherPublicContact = useGetPublicContact(otherHandles[0])
  // end postToApp data

  const { ready } = useContext(AppLocationContext)
  const [widgetTopicId, setWidgetTopicId] = useState<number | undefined>()

  // code for the live chat widget
  useEffect(() => {
    if (ready && widgetTopicId) {
      navToTopic(widgetTopicId, { widgetView: WIDGET_VIEW.MESSENGER })
      setWidgetTopicId(undefined)
    }
  }, [navToTopic, ready, widgetTopicId])

  useEffect(() => {
    const focus = focusRef.current
    if (replyTimeline && focus) {
      focus()
    }
  }, [replyTimeline, focusRef])

  const { handleTyping, clearIndicator } = useTypingIndicator()

  const clearReplyTimelineId = useCallback(() => {
    dispatch(setReplyTimelineId(topicId, undefined))
  }, [dispatch, topicId])

  const clearReplyOnClick = useCallback(() => {
    return Promise.resolve(clearReplyTimelineId)
  }, [clearReplyTimelineId])

  const postToApp = useCallback(() => {
    const messageData = {
      topicName: GROUP_TOPIC_TYPES.includes(topic?.participant_mode)
        ? topic.subject
        : name,
      topicId: topicId,
      participants: namesLookup,
      sender: currentMemberId,
      senderHandle: currentHandle,
      avatarUrl: otherPublicContact?.profile_image,
    }
    const messageSent = (window as any)?.webkit?.messageHandlers?.messageSent
    if (messageSent) {
      messageSent.postMessage(messageData)
    }
    console.info(messageData)
  }, [
    currentHandle,
    currentMemberId,
    name,
    namesLookup,
    otherPublicContact?.profile_image,
    topic?.participant_mode,
    topic?.subject,
    topicId,
  ])

  const onSubmit = useCallback(
    (html) => {
      setIsSubmitting(true)
      let promise: any
      if (activeType === 'message') {
        if (liveChatApp && !topicId && !directChatHandle) {
          promise = dispatch(axios(createNewLiveChat({ html }))).then(
            (result: any) => {
              const tId = parseInt(
                Object.keys(result.data.records.topics.data)[0],
                10,
              )
              setWidgetTopicId(tId)
            },
          )
        } else if (!topicId && directChatHandle) {
          promise = dispatch(
            axios(createDirectChat(directChatHandle, { html })),
          )
          promise.then((result: any) => {
            const directChatTopicId = findRecordInApiResponse(
              result,
              'topics',
            ).id
            navToDirectChat(directChatHandle, directChatTopicId)
          })
        } else {
          clearIndicator(topicId)
          promise = dispatch(
            axios(sendChat(topicId, { html }, replyTimeline?.id || null)),
          )
        }
      } else {
        promise = dispatch(axios(sendNote(topicId, { html })))
      }

      promise
        .then(() => {
          if (replyTimeline?.id) {
            clearReplyTimelineId()
          }
          postToApp()
        })
        .finally(() => setIsSubmitting(false))
        .catch(() => {
          // do nothing
        })
      return promise
    },
    [
      activeType,
      axios,
      clearIndicator,
      clearReplyTimelineId,
      directChatHandle,
      dispatch,
      liveChatApp,
      navToDirectChat,
      postToApp,
      replyTimeline?.id,
      topicId,
    ],
  )

  const onActiveTypeChanged = useCallback(() => {
    setActiveType((activeType) =>
      activeType === 'message' ? 'note' : 'message',
    )
  }, [])

  const getFocusFunc = useCallback((func) => (focusRef.current = func), [])

  const placeholder: string = useMemo(() => {
    if (activeType === 'note') {
      return NOTE_PLACEHOLDER
    }

    const directChatTarget = name || directChatHandle
    if (directChatTarget) {
      return `Message ${directChatTarget}`
    }

    return liveChatApp
      ? topicId
        ? 'Message'
        : 'Write a message'
      : `Message ${topic?.subject || 'everyone'}`
  }, [activeType, directChatHandle, liveChatApp, name, topic?.subject, topicId])

  return (
    <>
      <ConfirmEmailBanner />
      <ApiErrorMessages
        list={[
          {
            httpMethod: 'post',
            route: apiRoutes.admin_api_v1_topic_chats.path,
            uniqueKey: topicId,
            element: composerElement,
          },
          {
            httpMethod: 'post',
            route: apiRoutes.admin_api_v1_files.path,
            element: composerElement,
          },
        ]}
      />
      <Stack
        className={cn(
          containerStyles({
            variant: activeType,
            sending: isSubmitting,
            liveChatApp,
          }),
        )}
        ref={composerRefCallback}
      >
        {activeType === 'message' && replyTimeline && (
          <Stack
            direction="row"
            css={{
              backgroundColor: '$grey50',
              margin: '$3 $3 0',
              borderRadius: '$1',
            }}
          >
            <ReplyChatReference
              timeline={replyTimeline}
              className={replyContainerStyles()}
            />
            <Button
              variant="tertiary"
              shape="square"
              onClick={clearReplyOnClick}
              className="composer-container__replychatreference"
            >
              <MenuDeleteIcon />
            </Button>
          </Stack>
        )}

        <TrixForm
          id={`${topicId || 0}`}
          onChange={activeType === 'message' ? handleTyping : undefined}
          onSubmit={onSubmit}
          placeholder={placeholder}
          passUpFocusAtStart={getFocusFunc}
          onActiveTypeChanged={onActiveTypeChanged}
          activeType={activeType}
        />
      </Stack>
    </>
  )
}

const containerStyles = css({
  background: '$backgroundSecondary',
  padding: '0',
  position: 'relative',
  alignItems: 'flex-start',
  justifyContent: 'flex-end',
  '&::before': {
    content: '""',
    position: 'absolute',
    left: 0,
    top: 0,
    height: 1,
    width: '100%',
    animation: `none`,
    zIndex: 2,
    border: 0,
    borderTop: '1px solid',
    // eslint-disable-next-line max-len
    borderImageSource:
      'radial-gradient(circle at 100% 95%, #03f2c2, #4f8bff 34%, #f60575 65%, #ff672c 81%, #ffb207 97%, #ffb600 103%)',
    borderImageSlice: 1,
  },
  transition: 'opacity 120ms ease-out',
  variants: {
    variant: {
      message: {
        color: '$grey800 !important',
        fill: '$grey800 !important',
      },
      note: {
        backgroundColor: '#fffcb2',
        color: '$yellow900 !important',
        fill: '$yellow900 !important',
      },
    },
    sending: {
      true: {
        opacity: 0.5,
      },
    },
    liveChatApp: {
      true: {
        margin: '0 $4',
      },
    },
  },
  defaultVariants: {
    variant: 'message',
  },
})

const replyContainerStyles = css({
  marginBottom: 0,
  paddingBottom: '$2',
  maxHeight: '75px',
  overflow: 'hidden',
  alignItems: 'start',
})
